import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  AlertTriangle, 
  TrendingDown, 
  TrendingUp, 
  Clock, 
  CheckCircle2,
  Minus
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays, isBefore, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";

interface DelayMetrics {
  totalTasks: number;
  overdueTasks: number;
  percentageOverdue: number;
  averageDelayDays: number;
  trend: 'improving' | 'worsening' | 'stable';
  previousPercentage: number;
}

interface DelayIndexWidgetProps {
  isLoaded?: boolean;
}

export function DelayIndexWidget({ isLoaded = true }: DelayIndexWidgetProps) {
  const [metrics, setMetrics] = useState<DelayMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDelayMetrics();
  }, []);

  const fetchDelayMetrics = async () => {
    try {
      const today = startOfDay(new Date());

      // Fetch all tasks that are not concluded
      const { data: tasks, error } = await supabase
        .from('project_tasks')
        .select('id, name, end_date, status, updated_at')
        .neq('status', 'concluida');

      if (error) throw error;

      if (!tasks || tasks.length === 0) {
        setMetrics({
          totalTasks: 0,
          overdueTasks: 0,
          percentageOverdue: 0,
          averageDelayDays: 0,
          trend: 'stable',
          previousPercentage: 0
        });
        setLoading(false);
        return;
      }

      // Calculate overdue tasks
      const overdueTasks = tasks.filter(task => {
        if (!task.end_date) return false;
        const endDate = new Date(task.end_date);
        return isBefore(endDate, today);
      });

      // Calculate average delay days for overdue tasks
      let totalDelayDays = 0;
      overdueTasks.forEach(task => {
        if (task.end_date) {
          const endDate = new Date(task.end_date);
          const delayDays = differenceInDays(today, endDate);
          totalDelayDays += delayDays;
        }
      });

      const averageDelayDays = overdueTasks.length > 0 
        ? Math.round(totalDelayDays / overdueTasks.length * 10) / 10 
        : 0;

      const percentageOverdue = Math.round((overdueTasks.length / tasks.length) * 100);

      // Calculate trend by comparing to tasks updated in last 7 days
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      const recentlyCompletedTasks = tasks.filter(task => {
        const updatedAt = new Date(task.updated_at);
        return updatedAt >= oneWeekAgo && task.status === 'concluida';
      }).length;

      const recentlyBecameOverdue = overdueTasks.filter(task => {
        const endDate = new Date(task.end_date!);
        const daysOverdue = differenceInDays(today, endDate);
        return daysOverdue <= 7;
      }).length;

      let trend: 'improving' | 'worsening' | 'stable' = 'stable';
      if (recentlyCompletedTasks > recentlyBecameOverdue) {
        trend = 'improving';
      } else if (recentlyBecameOverdue > recentlyCompletedTasks) {
        trend = 'worsening';
      }

      setMetrics({
        totalTasks: tasks.length,
        overdueTasks: overdueTasks.length,
        percentageOverdue,
        averageDelayDays,
        trend,
        previousPercentage: percentageOverdue
      });
    } catch (error) {
      console.error('Error fetching delay metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthStatus = (percentage: number) => {
    if (percentage === 0) return { label: 'Excelente', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10' };
    if (percentage <= 10) return { label: 'Bom', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/10' };
    if (percentage <= 25) return { label: 'Atenção', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10' };
    return { label: 'Crítico', color: 'text-destructive', bg: 'bg-destructive/10' };
  };

  const getTrendIcon = () => {
    if (!metrics) return null;
    
    switch (metrics.trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />;
      case 'worsening':
        return <TrendingDown className="h-4 w-4 text-destructive" />;
      default:
        return <Minus className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getTrendLabel = () => {
    if (!metrics) return '';
    
    switch (metrics.trend) {
      case 'improving':
        return 'Melhorando';
      case 'worsening':
        return 'Piorando';
      default:
        return 'Estável';
    }
  };

  if (loading || !metrics) {
    return (
      <Card className="animate-pulse">
        <CardHeader className="pb-2">
          <div className="h-5 w-32 bg-muted rounded" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-12 bg-muted rounded" />
            <div className="h-4 w-24 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const status = getHealthStatus(metrics.percentageOverdue);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="relative overflow-hidden">
        {/* Background indicator */}
        <div 
          className={cn(
            "absolute inset-0 opacity-5",
            metrics.percentageOverdue > 25 ? "bg-destructive" : "bg-primary"
          )} 
        />
        
        <CardHeader className="pb-2 relative">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Índice de Atrasos
            </span>
            <div className={cn("px-2 py-0.5 rounded-full text-xs font-medium", status.bg, status.color)}>
              {status.label}
            </div>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="relative space-y-4">
          {/* Main metrics */}
          <div className="grid grid-cols-2 gap-4">
            {/* Percentage overdue */}
            <div className="space-y-1">
              <div className="flex items-baseline gap-1">
                <span className={cn("text-3xl font-bold tabular-nums", status.color)}>
                  {metrics.percentageOverdue}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Tarefas atrasadas
              </p>
              <p className="text-xs text-muted-foreground/70">
                {metrics.overdueTasks} de {metrics.totalTasks} tarefas
              </p>
            </div>

            {/* Average delay */}
            <div className="space-y-1">
              <div className="flex items-baseline gap-1">
                <span className={cn(
                  "text-3xl font-bold tabular-nums",
                  metrics.averageDelayDays > 7 ? "text-destructive" : 
                  metrics.averageDelayDays > 3 ? "text-amber-600 dark:text-amber-400" : 
                  "text-muted-foreground"
                )}>
                  {metrics.averageDelayDays}
                </span>
                <span className="text-sm text-muted-foreground">dias</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Atraso médio
              </p>
            </div>
          </div>

          {/* Trend indicator */}
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <div className="flex items-center gap-2">
              {getTrendIcon()}
              <span className="text-sm text-muted-foreground">{getTrendLabel()}</span>
            </div>
            {metrics.overdueTasks > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-1 text-xs text-muted-foreground"
              >
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                <span>{metrics.overdueTasks} pendentes</span>
              </motion.div>
            )}
            {metrics.overdueTasks === 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"
              >
                <CheckCircle2 className="h-3 w-3" />
                <span>Sem atrasos</span>
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
